<template>
  <q-page class="q-pa-xl centered-container">
    <div class="row items-start justify-between q-mb-lg">
      <div class="col">
        <div class="text-h4 q-mb-xs">
          {{ i18n.page_title }}
        </div>
        <div class="text-body2 text-secondary">
          {{ i18n.page_subtitle }}
        </div>
      </div>
      <div class="col-auto row items-center q-gutter-sm">
        <q-btn
          color="primary"
          :disable="!fec_add_enabled"
          icon="sym_o_note_add"
          unelevated
          :label="i18n.fec_action_create"
          no-caps
          @click="form_dialog"
        />
      </div>
    </div>

    <!-- FEC ITEMS -->
    <div
      v-if="fecs.length > 0"
      class="row q-col-gutter-lg q-mt-md justify-start"
    >
      <div
        v-for="fec in fecs"
        :key="fec.id"
        class="col-12 self-stretch"
      >
        <q-card class="fit text-center">
          <q-card-section
            horizontal
            class=""
          >
            <q-card-section class="col-shrink bg-card-item p-1">
              <div class="fec-item">
                <span class="fec-item-fiscal-year">FEC <br>{{ fec.fiscal_year }}</span>
                <img
                  :alt="fec.cover_text"
                  :src="fec.cover_image"
                  class="fec-item-cc-logo"
                  style="height: 12px"
                >
              </div>
            </q-card-section>

            <q-card-section class="col text-left fec-card-body">
              <div class="row full_width items-center">
                <div class="col">
                  <p
                    class="card-title q-mb-none"
                    v-html="fec.report_range"
                  />
                </div>

                <div class="col-shrink q-mr-sm">
                  <q-chip
                    :label="fec.valuation_method === 'fifo' ? i18n.fec_valuation_method_fifo_short : i18n.fec_valuation_method_cump_short"
                   
                    color="surface"
                    text-color="primary"
                    class="text-medium"
                    square
                  />
                </div>

                <div class="col-shrink">
                  <!-- Menu (Rerun / Delete) -->
                  <q-btn
                    flat
                    round
                    icon="las la-ellipsis-v"
                  >
                    <q-menu>
                      <q-list>
                        <!-- Rerun -->
                        <q-item
                          v-close-popup
                          clickable
                          :disable="fec.status == 'processing' || !fecs_feature_permitted"
                          style="min-width: 200px"
                          @click="fecs_rerun(fec)"
                        >
                          <q-item-section avatar>
                            <q-icon
                              color="primary"
                              name="las la-redo"
                            />
                          </q-item-section>
                          <q-item-section>
                            <q-item-label>{{ i18n.fec_action_rerun }}</q-item-label>
                          </q-item-section>
                        </q-item>

                        <!-- Delete -->
                        <q-item
                          v-close-popup
                          clickable
                          style="min-width: 200px"
                          @click="fecs_delete_confirm(fec)"
                        >
                          <q-item-section avatar>
                            <q-icon
                              color="negative"
                              name="las la-trash"
                            />
                          </q-item-section>
                          <q-item-section>
                            <q-item-label>{{ i18n.fec_action_delete }}</q-item-label>
                          </q-item-section>
                        </q-item>
                      </q-list>
                    </q-menu>
                  </q-btn>
                </div>
              </div>

              <template v-if="fec.status != 'processing'">
                <q-item class="q-px-none">
                  <q-item-section>
                    <q-item-label class="">
                      {{ i18n.fec_transactions_number }}
                      <strong>
                        {{ fec.transactions_count }}
                      </strong>
                    </q-item-label>

                    <q-item-label
                      v-if="fec.status == 'success'"
                      class="text-caption "
                    >
                      {{ fec.generated_at }}
                    </q-item-label>

                    <q-item-label
                      v-if="fec.status == 'error'"
                      class="text-negative text-bold"
                    >
                      {{ i18n.fec_status_error }}
                    </q-item-label>

                    <q-item-label
                      v-if="fec.warnings.length > 0"
                      class="q-pt-md"
                    >
                      <q-btn
                        class="bg-amber-1"
                        color="black"
                        flat
                        unelevated
                        icon="las la-exclamation-triangle"
                        :label="fec.warnings_summary"
                        @click="warning_dialog(fec)"
                      />
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>

              <div class="text-center">
                <template v-if="fec.status == 'processing'">
                  <div class="q-mt-sm">
                    {{ i18n.fec_status_generating }}
                    <q-btn
                      loading
                      flat
                    >
                      <q-spinner-tail class="q-mr-md" />
                    </q-btn>
                  </div>
                </template>
              </div>

              <div class="text-right fec-card-actions">
                <template v-if="fec.status == 'success'">
                  <a
                    v-if="fec.files_excel_url"
                    :href="fec.files_excel_url"
                    target="_blank"
                    download
                  >
                    <q-btn
                      outline
                      icon="las la-file-excel"
                      class="q-mt-sm"
                      :label="i18n.fec_download_excel_report"
                      :disable="!fec.files_excel_url"
                      unelevated
                      no-caps
                    />
                  </a>

                  <a
                    :href="fec.files_fec_url"
                    target="_blank"
                    download
                  >
                    <q-btn
                      icon="sym_o_download"
                      class="q-mt-sm q-ml-md"
                      :label="i18n.fec_download_fec_file"
                      :disable="!fec.files_fec_url"
                      unelevated
                      color="primary"
                      no-caps
                    />
                  </a>
                </template>
              </div>
            </q-card-section>
          </q-card-section>
        </q-card>
      </div>
    </div>

    <!-- No fecs -->
    <div
      v-if="fecs.length == 0"
      class="row full_width justify-center q-mt-xl q-pt-xl align-center fit fill-height"
    >
      <div class="col-12">
        <div class="row">
          <div class="col-xs-12 col-sm-12 col-md-5 gt-sm">
            <q-img
              src="/illustrations/fec_description_illustration.svg"
              style="max-width: 80%; height: auto"
              class="q-pa-xl"
            />
          </div>

          <div class="col-xs-12 col-sm-12 col-md-7 self-center">
            <div class="row items-center q-mb-sm">
              <div class="col-shrink">
                <span class="text-overline">{{ i18n.page_feature_availability_from }} </span>
              </div>
              <div class="col-shrink">
                <q-chip
                  class="q-ml-sm q-py-xs"
                  color="pastel-blue"
                  style="position: relative; top: -1px"
                  text-color="text-default"
                  label="Basic"
                  size="sm"
                />
              </div>
            </div>

            <span class="text-h4"> {{ i18n.page_title }} </span>
            <p class=" q-my-md">
              {{ i18n.page_business_description }}
            </p>

            <ul class=" q-pl-lg">
              <li v-html="i18n.page_business_description_point_1" />
              <li v-html="i18n.page_business_description_point_2" />
            </ul>

            <q-banner
              class="bg-pastel-green q-px-md q-mt-lg"
              dense
            >
              <template v-slot:avatar>
                <q-icon
                  name="las la-info-circle"
                  class="message-box-text"
                  size="sm"
                />
              </template>
              <div
                class="text-caption message-box-text"
                style="font-weight: 400"
              >
                {{ i18n.page_individual_description }}
              </div>
            </q-banner>

            <a
              href="/examples/exemple_de_FEC_2023.txt"
              target="_blank"
              download
            >
              <q-btn
                class="q-mt-lg"
                color="primary"
                outline
                unelevated
                :label="i18n.page_fec_example_label"
                icon="get_app"
                no-caps
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  </q-page>

  <!-- New fec dialog -->
  <q-dialog v-model="form_dialog_show">
    <q-card
      class="q-pa-lg"
      style="width: 420px; max-width: 80vw"
    >
      <div class="q-card shadow-borderrow wrap justify-start q-pa-sm">
        <q-form
          class="fit row wrap justify-center items-start content-start"
          @submit.prevent="fecs_create"
        >
          <div class="col-12 q-mb-lg">
            <span class="text-h6">{{ i18n.fec_action_create }}</span>
          </div>

          <div class="col-12">
            <span class="text-input-label">{{ i18n.fec_field_fiscal_year }}</span>
            <q-select
              v-model="form_fiscal_year"
              class="full-width q-mt-sm"
              option-label="year"
              outlined
              dense
              hint=" "
              :options="fiscal_years"
              :error="form_errors?.fiscal_year?.length >= 1"
              :error-message="(form_errors?.fiscal_year || [])[0]"
              options-dense
            />
          </div>

          <div class="col-12">
            <span class="text-input-label">{{ i18n.fec_field_date_start }}</span>
            <q-input
              v-model="form_date_from"
              outlined
              dense
              class="full-width q-mt-sm"
              :error="form_errors?.from?.length >= 1"
              :error-message="(form_errors?.from || [])[0]"
              :mask="date_mask"
              :rules="[rule_date, rule_from_before_to]"
            >
              <template v-slot:append>
                <q-icon
                  name="event"
                  class="cursor-pointer"
                >
                  <q-popup-proxy
                    cover
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-date
                      v-model="form_date_from"
                      :mask="date_format"
                    >
                      <div class="row items-center justify-end">
                        <q-btn
                          v-close-popup
                          :label="$q.lang.label.close"
                          color="primary"
                          flat
                        />
                      </div>
                    </q-date>
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>

          <div class="col-12">
            <span class="text-input-label">{{ i18n.fec_field_date_end }}</span>
            <q-input
              v-model="form_date_to"
              outlined
              dense
              class="full-width q-mt-sm"
              :error="form_errors?.to?.length >= 1"
              :error-message="(form_errors?.to || [])[0]"
              :mask="date_mask"
              :rules="[rule_date, rule_to_after_from]"
            >
              <template v-slot:append>
                <q-icon
                  name="event"
                  class="cursor-pointer"
                >
                  <q-popup-proxy
                    cover
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-date
                      v-model="form_date_to"
                      :mask="date_format"
                    >
                      <div class="row items-center justify-end">
                        <q-btn
                          v-close-popup
                          :label="$q.lang.label.close"
                          color="primary"
                          flat
                        />
                      </div>
                    </q-date>
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>

          <div class="col-12 q-mt-md">
            <q-banner
              class="bg-pastel-green fit"
              dense
            >
              <template v-slot:avatar>
                <q-icon
                  name="las la-info-circle"
                  class="message-box-text"
                  size="sm"
                />
              </template>
              <div class="text-caption message-box-text">
                {{ workspace_closing_date }}
              </div>
            </q-banner>
          </div>

          <div class="col-12">
            <span class="text-input-label">{{ i18n.fec_field_valuation_method }}</span>
            <q-select
              v-model="form_valuation_method"
              class="full-width q-mt-sm"
              outlined
              dense
              :options="[
                { label: i18n.fec_valuation_method_cump, value: 'cump' },
                { label: i18n.fec_valuation_method_fifo, value: 'fifo' }
              ]"
              emit-value
              map-options
            />
          </div>

          <div class="col-12">
            <q-btn
              vertical
              type="submit"
              class="q-mt-md full-width"
              :label="i18n.fec_form_submit"
              color="primary"
              unelevated
            />
          </div>
        </q-form>
      </div>
    </q-card>
  </q-dialog>

  <q-dialog v-model="warning_dialog_show">
    <q-card
      class="q-pa-md q-pt-lg"
      style="width: 700px; max-width: 80vw"
    >
      <div class="row text-center">
        <div class="col-12 text-center">
          <q-avatar
            class="bg-pastel-orange"
            size="56px"
          >
            <q-icon
              name="las la-exclamation-triangle"
              color="black"
              size="lg"
            />
          </q-avatar>
        </div>

        <div class="col-12 q-py-md">
          <span class="text-h6">{{ i18n.warnings_title }}</span>
          <p class="q-pt-sm text-caption ">
            {{ warning_dialog_fec.warnings_summary }}
          </p>
        </div>
        <q-list
          class="text-left"
          dense
        >
          <q-item
            v-for="(warning, index) in warning_dialog_fec.warnings"
            :key="`warning-${index}`"
          >
            <q-item-section avatar>
              <q-icon
                name="las la-exclamation-triangle"
                color="warning"
              />
            </q-item-section>
            <q-item-section>
              <q-item-label>
                {{ warning }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>

        <q-banner
          class="bg-pastel-purple q-px-md q-mx-md q-my-lg text-left fit"
          dense
        >
          <template v-slot:avatar>
            <q-icon
              name="las la-info-circle"
              size="md"
            />
          </template>
          <div
            class=""
            v-html="i18n.warnings_note"
          />
        </q-banner>
      </div>
      <q-separator />
      <div class="row q-mt-lg q-px-md justify-end">
        <q-btn
          :label="$q.lang.label.close"
          flat
          class="q-mr-sm btn-underline"
          color="primary"
          unelevated
          @click="warning_dialog_show = false"
        />
      </div>
    </q-card>
  </q-dialog>

  <q-dialog v-model="subscription_dialog_show">
    <q-card
      class="q-pa-xl"
      style="width: 700px; max-width: 80vw"
    >
      <div class="row text-center">
        <div class="col-12 text-center">
          <q-icon
            name="las la-file-invoice-dollar"
            size="56px"
            class=""
            color="primary"
          />
        </div>
        <div class="col-12 q-pt-lg">
          <span class="text-h6">{{ i18n.subscription_dialog_title }}</span>

          <p
            v-if="!fecs_feature_enabled"
            class="q-pt-sm"
            v-html="i18n.subscription_dialog_description"
          />
          <p
            v-else-if="!fecs_feature_permitted"
            class="q-pt-sm"
            v-html="$page.props.shared.banner"
          />

          <div class="col-12">
            <Link :href="$page.props.subscription_dialog_action">
              <q-btn
                color="positive"
                class="q-mt-lg q-mx-md"
                icon="las la-shopping-cart"
                unelevated
                :label="i18n.subscription_dialog_subscribe"
                no-caps
              />
            </Link>

            <q-btn
              color="primary"
              class="q-mt-lg q-mx-md"
              icon="las la-envelope"
              unelevated
              :label="i18n.subscription_dialog_contact"
              no-caps
              href="http://comptacrypto.com/contact"
              target="_blank"
            />
          </div>

          <div class="col-12 q-mt-lg">
            <q-btn
              :label="$q.lang.label.cancel"
              flat
              size="sm"
              @click="subscription_dialog_show = false"
            />
          </div>
        </div>
      </div>
    </q-card>
  </q-dialog>
</template>
<script>
import Layout from '@/layouts/workspace.vue';
import { usePoll } from '@inertiajs/vue3';
import { Link } from '@inertiajs/vue3';

export default {
  components: {
    Link,
  },
  layout: Layout,
  props: {
    i18n: {
      type: Object,
      required: true,
    },
    date_format: {
      type: String,
      required: true,
    },
    date_mask: {
      type: String,
      required: true,
    },
    action_create_path: {
      type: String,
      required: true,
    },
    fec_polling: {
      type: Boolean,
      required: true,
    },
    fecs: {
      type: Array,
      required: true,
    },
    fiscal_years: {
      type: Array,
      required: true,
    },
    workspace_closing_date: {
      type: String,
      required: true,
    },
    fecs_feature_enabled: {
      type: Boolean,
      required: true,
    },
    fecs_feature_permitted: {
      type: Boolean,
      required: true,
    },
    fec_add_enabled: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    const poll = usePoll(
      3000,
      {
        only: ["fec_polling", "fecs"],
        data: { ajax_bar_skip: '' },
      },
      { autoStart: this.fec_polling }
    );

    return {
      form_dialog_show: false,
      subscription_dialog_show: false,
      warning_dialog_show: false,
      warning_dialog_fec: null,
      fecs_polling_start: poll.start,
      fecs_polling_stop: poll.stop,
      form_errors: [],
      form_date_from: '',
      form_date_to: '',
      form_fiscal_year: '',
      form_valuation_method: 'cump',
    };
  },
  computed: {
  },
  watch: {
    fec_polling: {
      handler(new_value) {
        if (new_value) {
          this.fecs_polling_start();
        } else {
          this.fecs_polling_stop();
        }
      },
      immediate: true,
    },
    form_fiscal_year(new_value) {
      this.form_date_from = new_value.from;
      this.form_date_to = new_value.to;
    },
  },
  mounted() {
  },
  methods: {
    rule_from_before_to: function () {
      //todo
    },
    rule_to_after_from: function () {
      //todo
    },
    form_dialog: function () {
      if (this.fecs_feature_permitted) {
        this.form_date_from = '';
        this.form_date_to = '';
        this.form_fiscal_year = '';
        this.form_errors = [];
        this.form_dialog_show = true;
      } else {
        this.subscription_dialog_show = true;
      }
    },
    fecs_create: function () {
      this.$inertia.post(
        this.action_create_path,
        {
          from: this.form_date_from,
          to: this.form_date_to,
          fiscal_year: this.form_fiscal_year.year,
          valuation_method: this.form_valuation_method,
        },
        {
          preserveScroll: true,
          onStart: () => {
          },
          onSuccess: () => {
            this.form_dialog_show = false;
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.form_errors = errors;
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
          },
        }
      );
    },
    fecs_rerun: function (fec) {
      this.$inertia.post(
        fec.action_rerun_path,
        {},
        {
          preserveScroll: true,
          onStart: () => {
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
          },
        }
      );
    },
    fecs_delete_confirm: function (fec) {
      this.$q.dialog({
        title: this.i18n.fec_delete_confirm_title,
        message: `${fec.fiscal_year} \n\n ${this.i18n.fec_delete_confirm_message}`,
        cardClass: "whitespace-pre-line",
        cancel: true,
        persistent: true,
      }).onOk(() => {
        this.fecs_delete(fec);
      })
    },
    fecs_delete: function (fec) {
      this.$inertia.delete(
        fec.action_destroy_path,
        {
          preserveScroll: true,
          onStart: () => {
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
          },
        }
      );
    },
    warning_dialog: function (fec) {
      this.warning_dialog_show = true;
      this.warning_dialog_fec = fec;
    },
  },
};
</script>
